// ── Catalog Loader ──────────────────────────────────────────────────
// Fetches catalog data from API endpoints, caches in-memory.
// Call loadCatalogs() during app init before using getters.

import {
  api_v1_spray_heads_path,
  api_v1_weather_stations_path,
  api_v1_series_path,
  api_v1_fittings_path,
  api_v1_nozzles_path,
  api_v1_valves_path,
  api_v1_drip_types_path,
} from "../shared/api-routes";

const CATALOG_FETCH_TIMEOUT_MS = 10000;

// ── API response shapes (Grape entities → JSON over the wire) ─────────
// Each interface mirrors the corresponding Entities::*Entity in
// app/api/entities/. CatalogEntity exposes { id, name, brand }; subclass
// columns are listed below. `nullable: true` columns (and DB columns
// without a NOT NULL constraint) map to `T | null | undefined`.

interface ApiCatalogBase {
  id: number;
  name: string;
  brand: string | null;
}

interface ApiSprayHead extends ApiCatalogBase {
  series: string | null;
  inlet_size: string | null;
  height: number | null;
  pop_up_height: number | null;
  spray_type: string | null;
  regulated_pressure: number | null;
}

interface ApiWeatherStation extends ApiCatalogBase {
  external_id: string;
  station_type: string;
  wireless: boolean;
  price_range: string | null;
}

interface ApiSeries {
  id: number;
  code: string;
  // jsonb column, seeded from db/catalogs/misc/series.json as a list of
  // model names ("MP1000", "MP2000", …).
  models: string[];
  // Nominal operating pressure in bar. Null for a series whose spec sheet
  // has not been captured yet.
  pressure: number | null;
}

interface ApiFitting extends ApiCatalogBase {
  fitting_type: string;
  angle: number | null;
  size: number | null;
  material: string | null;
}

interface ApiNozzleArc {
  arc: number;
  radius: number;
  flow: number;
  precipitation: number;
}

interface ApiNozzle {
  id: number;
  name: string;
  series: string | null;
  radius_min: number;
  radius_max: number;
  color: string;
  description: string | null;
  shape: string | null;
  width: number | null;
  length: number | null;
  position: string | null;
  brand_slug: string | null;
  brand_name: string | null;
  arcs: ApiNozzleArc[];
}

interface ApiValve extends ApiCatalogBase {
  capacity: number | null;
  valve_type: string | null;
  inlet_size: string | null;
  size: string | null;
  size_mm: number | null;
  max_flow: number | null;
  min_flow: number | null;
  flow_control: boolean | null;
  pressure_max: number | null;
  description: string | null;
}

interface ApiDripType extends ApiCatalogBase {
  series: string | null;
  drip_type: string;
  diameter: number;
  emitter_spacing: number;
  flow_per_emitter: number;
  pressure_min: number;
  pressure_max: number;
  max_length: number;
  filtration_requirement: number | null;
  color: string;
  description: string;
}

// ── Transformed (client-side) shapes ─────────────────────────────────
// These mirror the legacy hand-coded objects that the planner expects.

interface SprayBodyEntry {
  brand: string | null;
  height: number | null;
  type: string | null;
  inlet: string | null;
  riser: number | null;
  regulatedPressure?: number;
}

interface WeatherStationEntry {
  id: string;
  name: string;
  brand: string | null;
  type: string;
  wireless: boolean;
  price_range: string | null;
}

// Series flattens Series#models JSONB onto the row; callers read `.key`
// plus arbitrary string keys (e.g. `label`, `pressure`), so we keep the
// index signature loose.
interface SeriesEntry {
  key: string;
  [k: string]: unknown;
}

interface FittingEntry {
  type: string;
  angle?: number;
  size?: number;
  material: string | null;
  brand: string | null;
}

interface PresetArcEntry {
  radius: number;
  flow: number;
  precip: number;
}

interface PresetEntry {
  series: string | null;
  radiusMin: number;
  radiusMax: number;
  color: string;
  arcs: Record<string, PresetArcEntry>;
  description?: string;
  shape?: string;
  width?: number;
  length?: number;
  position?: string;
}

interface ValveEntry {
  name: string;
  type: string | null;
  size: string | null;
  sizeMm: number | null;
  maxFlow: number | null;
  minFlow: number | null;
  pressureMax: number | null;
  flowControl: boolean;
  description: string | null;
}

interface DripTypeEntry {
  brand: string | null;
  type: string;
  diameter: number;
  emitterSpacing: number;
  flowPerEmitter: number;
  pressureMin: number;
  pressureMax: number;
  maxLength: number;
  color: string;
  description: string;
  series?: string;
  filtrationRequirement?: number;
}

// In-memory cache
let _sprayBodies: Record<string, SprayBodyEntry> | null = null;
let _weatherStations: WeatherStationEntry[] | null = null;
let _series: SeriesEntry[] | null = null;
let _fittings: Record<string, FittingEntry> | null = null;
let _presets: Record<string, PresetEntry> | null = null;
let _valves: Record<string, ValveEntry> | null = null;
let _dripTypes: Record<string, DripTypeEntry> | null = null;
let _loaded = false;
let _loadError: Error | null = null;

/**
 * Minimal fallback preset used when the catalog API is unreachable. Keeps
 * the planner functional (rendering sprinklers, computing coverage) instead
 * of crashing with TypeError when logic.getArcData reads `preset.arcs[arc]`
 * on a missing preset. Mirrors the shape returned by transformPresets().
 */
export const FALLBACK_PRESET = {
  series: "MP Rotator",
  radiusMin: 2.5,
  radiusMax: 3.5,
  color: "#2196f3",
  arcs: {
    90: { radius: 3, flow: 0.19, precip: 14 },
    180: { radius: 3, flow: 0.38, precip: 14 },
    270: { radius: 3, flow: 0.57, precip: 14 },
    360: { radius: 3, flow: 0.76, precip: 14 },
  },
} as const;

/** Fetch with a hard timeout so a hung catalog endpoint doesn't block the
 *  planner forever. Uses AbortController (available in all modern browsers
 *  and in node/jsdom test environments). */
async function fetchJSON(url: string): Promise<unknown> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), CATALOG_FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Transform API spray heads array into keyed object matching
 * the old SPRAY_BODIES format: { "Hunter PROS-02": { brand, height, type, inlet, riser, regulatedPressure? } }
 */
function transformSprayBodies(apiData: ApiSprayHead[]): Record<string, SprayBodyEntry> {
  const result: Record<string, SprayBodyEntry> = {};
  for (const item of apiData) {
    result[item.name] = {
      brand: item.brand,
      height: item.height,
      type: item.spray_type,
      inlet: item.inlet_size,
      riser: item.pop_up_height,
      ...(item.regulated_pressure !== null && item.regulated_pressure !== undefined && { regulatedPressure: item.regulated_pressure }),
    };
  }
  return result;
}

/**
 * Transform API weather stations array into the old WEATHER_STATIONS format:
 * [{ id, name, brand, type, wireless, range, price_range, sensors?, depth? }]
 */
function transformWeatherStations(apiData: ApiWeatherStation[]): WeatherStationEntry[] {
  return apiData.map((item) => ({
    id: item.external_id,
    name: item.name,
    brand: item.brand,
    type: item.station_type,
    wireless: item.wireless,
    price_range: item.price_range,
  }));
}

/**
 * Transform API series array into the old SERIES format:
 * [{ key, label, pressure }]
 */
function transformSeries(apiData: ApiSeries[]): SeriesEntry[] {
  // `...item.models` spread an *array* into the object literal, producing
  // numeric keys ({0: "MP1000", 1: "MP2000"}) and no `models` property at
  // all — and it never carried `pressure`, so every consumer read undefined.
  // getSeriesForModel().pressure then rendered as "NaN bar" in the sprinkler
  // panel, and the zone hydraulics compared available pressure against 0,
  // reporting every zone as sufficient.
  return apiData.map((item) => ({
    key: item.code,
    models: item.models,
    pressure: item.pressure,
  }));
}

/**
 * Transform API fittings array into keyed object matching
 * the old FITTINGS format: { "Elbow-25": { type, angle, size, material, brand, ... } }
 */
function transformFittings(apiData: ApiFitting[]): Record<string, FittingEntry> {
  const result: Record<string, FittingEntry> = {};
  for (const item of apiData) {
    result[item.name] = {
      type: item.fitting_type,
      ...(item.angle !== null && item.angle !== undefined && { angle: item.angle }),
      ...(item.size !== null && item.size !== undefined && { size: item.size }),
      material: item.material,
      brand: item.brand,
    };
  }
  return result;
}

/**
 * Transform API nozzles array into the old PRESETS keyed-object format:
 * { "MP1000": { series, description, radiusMin, radiusMax, color, shape?, width?, length?, position?, arcs: { 90: { radius, flow, precip } } } }
 */
function transformPresets(apiData: ApiNozzle[]): Record<string, PresetEntry> {
  const result: Record<string, PresetEntry> = {};
  for (const item of apiData) {
    const arcsObj: Record<string, PresetArcEntry> = {};
    for (const arc of (item.arcs || [])) {
      arcsObj[arc.arc] = {
        radius: arc.radius,
        flow: arc.flow,
        precip: arc.precipitation,
      };
    }
    const entry: PresetEntry = {
      series: item.series,
      radiusMin: item.radius_min,
      radiusMax: item.radius_max,
      color: item.color,
      arcs: arcsObj,
    };
    if (item.description !== null && item.description !== undefined) entry.description = item.description;
    if (item.shape !== null && item.shape !== undefined) entry.shape = item.shape;
    if (item.width !== null && item.width !== undefined) entry.width = item.width;
    if (item.length !== null && item.length !== undefined) entry.length = item.length;
    if (item.position !== null && item.position !== undefined) entry.position = item.position;
    result[item.name] = entry;
  }
  return result;
}

/**
 * Transform API valves array into the old VALVES keyed-object format:
 * { "PGV-100": { name, type, size, sizeMm, maxFlow, minFlow, pressureMax, flowControl, description } }
 */
function transformValves(apiData: ApiValve[]): Record<string, ValveEntry> {
  const result: Record<string, ValveEntry> = {};
  for (const item of apiData) {
    result[item.name] = {
      name: item.name,
      type: item.valve_type,
      size: item.inlet_size || item.size,
      sizeMm: item.size_mm,
      maxFlow: item.max_flow || item.capacity,
      minFlow: item.min_flow,
      pressureMax: item.pressure_max,
      flowControl: item.flow_control || false,
      description: item.description,
    };
  }
  return result;
}

/**
 * Transform API drip types array into the old DRIP_TYPES keyed-object format:
 * { "Hunter HDL": { brand, series?, type, diameter, emitterSpacing, flowPerEmitter, pressureMin, pressureMax, maxLength, filtrationRequirement?, color, description } }
 */
function transformDripTypes(apiData: ApiDripType[]): Record<string, DripTypeEntry> {
  const result: Record<string, DripTypeEntry> = {};
  for (const item of apiData) {
    const entry: DripTypeEntry = {
      brand: item.brand,
      type: item.drip_type,
      diameter: item.diameter,
      emitterSpacing: item.emitter_spacing,
      flowPerEmitter: item.flow_per_emitter,
      pressureMin: item.pressure_min,
      pressureMax: item.pressure_max,
      maxLength: item.max_length,
      color: item.color,
      description: item.description,
    };
    if (item.series !== null && item.series !== undefined) entry.series = item.series;
    if (item.filtration_requirement !== null && item.filtration_requirement !== undefined) entry.filtrationRequirement = item.filtration_requirement;
    result[item.name] = entry;
  }
  return result;
}

/**
 * Load all catalog data from API endpoints in parallel.
 * Call once during app initialization. Safe to call multiple times (no-op after first load).
 */
export async function loadCatalogs() {
  if (_loaded) return;

  try {
    const [
      sprayBodiesRaw,
      weatherStationsRaw,
      seriesRaw,
      fittingsRaw,
      nozzlesRaw,
      valvesRaw,
      dripTypesRaw,
    ] = await Promise.all([
      fetchJSON(api_v1_spray_heads_path()),
      fetchJSON(api_v1_weather_stations_path()),
      fetchJSON(api_v1_series_path()),
      fetchJSON(api_v1_fittings_path()),
      fetchJSON(api_v1_nozzles_path()),
      fetchJSON(api_v1_valves_path()),
      fetchJSON(api_v1_drip_types_path()),
    ]);

    // fetchJSON returns `unknown` (raw JSON); each endpoint is contractually
    // an array of the matching ApiXxx shape (see Entities::* in
    // app/api/entities/). We cast at the boundary so the transforms can
    // rely on strongly-typed inputs.
    _sprayBodies = transformSprayBodies(sprayBodiesRaw as ApiSprayHead[]);
    _weatherStations = transformWeatherStations(weatherStationsRaw as ApiWeatherStation[]);
    _series = transformSeries(seriesRaw as ApiSeries[]);
    _fittings = transformFittings(fittingsRaw as ApiFitting[]);
    _presets = transformPresets(nozzlesRaw as ApiNozzle[]);
    _valves = transformValves(valvesRaw as ApiValve[]);
    _dripTypes = transformDripTypes(dripTypesRaw as ApiDripType[]);
    _loaded = true;
    _loadError = null;
  } catch (err) {
    _loadError = err as Error;
    console.error('[catalog-loader] Failed to load catalogs:', (err as Error).message || err);
    // Show one toast with a retry affordance. Lazy-import showToast to avoid
    // a circular dependency at module load time.
    try {
      const { showToast } = await import('../shared/utils.ts');
      const { t } = await import('../shared/i18n.ts');
      // Use a catalog-specific key when available, otherwise fall through to
      // the existing generic "projectSaveFailed" copy (present in every
      // locale) so we always have a translated message.
      const key = 'errors.catalogLoadFailed';
      const translated = t(key);
      const msg = translated === key ? t('errors.projectSaveFailed') : translated;
      showToast(msg, 'error');
      // Expose a retry function on the document so the toast / a DevTools
      // user can trigger a retry without reloading the page.
      (window as unknown as { __smartpluvia_retryCatalogs?: () => Promise<void> })
        .__smartpluvia_retryCatalogs = retryLoadCatalogs;
    } catch { /* toast is best-effort */ }
  }
}

/** Reset the loaded flag and re-run loadCatalogs(). Bound to window for
 *  manual retries after a network blip. */
export async function retryLoadCatalogs() {
  _loaded = false;
  _loadError = null;
  await loadCatalogs();
}

// Getter return types stay as `unknown` to preserve the legacy public
// contract — callers across the planner (render.ts, logic.ts, events.ts,
// three-view.ts, bom.ts) narrow the result with their own casts. The
// internal cache types above remain strongly typed.
export function getSprayBodies(): unknown {
  return _sprayBodies;
}

export function getWeatherStations(): unknown {
  return _weatherStations;
}

export function getSeries(): unknown {
  return _series;
}

export function getFittings(): unknown {
  return _fittings;
}

export function getPresets(): unknown {
  return _presets;
}

export function getValves(): unknown {
  return _valves;
}

export function getDripTypes(): unknown {
  return _dripTypes;
}

/** Returns true if catalogs have been loaded. */
export function isCatalogsLoaded() {
  return _loaded;
}

/**
 * Reset cached data (useful for tests).
 */
export function resetCatalogs() {
  _sprayBodies = null;
  _weatherStations = null;
  _series = null;
  _fittings = null;
  _presets = null;
  _valves = null;
  _dripTypes = null;
  _loaded = false;
}

/**
 * Inject catalog data directly (for tests, no HTTP needed).
 * Pass only the catalogs you need; others remain unchanged.
 *
 * The parameter type uses `unknown` per-field because test fixtures
 * supply legacy/handcrafted shapes that don't always match the API
 * shape exactly (e.g. additional `range`, `sensors`, `depth` columns
 * on weather stations); each cache slot is cast at assignment so the
 * getter types stay accurate.
 */
export function setCatalogs({
  sprayBodies,
  weatherStations,
  series,
  fittings,
  presets,
  valves,
  dripTypes,
}: {
  sprayBodies?: unknown;
  weatherStations?: unknown;
  series?: unknown;
  fittings?: unknown;
  presets?: unknown;
  valves?: unknown;
  dripTypes?: unknown;
} = {}) {
  if (sprayBodies !== undefined) _sprayBodies = sprayBodies as Record<string, SprayBodyEntry>;
  if (weatherStations !== undefined) _weatherStations = weatherStations as WeatherStationEntry[];
  if (series !== undefined) _series = series as SeriesEntry[];
  if (fittings !== undefined) _fittings = fittings as Record<string, FittingEntry>;
  if (presets !== undefined) _presets = presets as Record<string, PresetEntry>;
  if (valves !== undefined) _valves = valves as Record<string, ValveEntry>;
  if (dripTypes !== undefined) _dripTypes = dripTypes as Record<string, DripTypeEntry>;
  _loaded = true;
}
